import clsx from "clsx";
import { ArrowLeft, ArrowRight, Check, LoaderCircle, Sparkles } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import type { AlertReview, AlertReviewQuestion } from "../../watchlist/types";
import type { WatchSetupAnswer } from "../../watchlist/watchSetupState";

type WatchSetupWizardProps = {
  review: AlertReview;
  round: number;
  questionIndex: number;
  answers: Record<string, WatchSetupAnswer>;
  onAnswer: (questionId: string, answer: WatchSetupAnswer) => void;
  onNext: () => void;
  onBack: () => void;
  busy?: boolean;
};

const Choice = ({
  selected,
  onClick,
  title,
  description,
  recommended = false,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description?: string;
  recommended?: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-tour={recommended ? "wizard-recommended-choice" : undefined}
      onClick={onClick}
      className={clsx(
        "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        selected
          ? "border-primary/60 bg-primary/[0.06]"
          : "border-outline bg-surface hover:border-primary/30 hover:bg-muted-surface/50",
      )}
    >
      <span
        className={clsx(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-primary bg-primary text-white" : "border-outline bg-background",
        )}
      >
        {selected && <Check size={12} aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-text">
          {title}
          {recommended && (
            <span className="rounded-full bg-info/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-info">
              {t("setup_recommended")}
            </span>
          )}
        </span>
        {description && <span className="mt-0.5 block text-xs text-text-dim">{description}</span>}
      </span>
    </button>
  );
};

const QuestionStep = ({
  question,
  round,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack,
  busy,
}: {
  question: AlertReviewQuestion;
  round: number;
  index: number;
  total: number;
  answer?: WatchSetupAnswer;
  onAnswer: (answer: WatchSetupAnswer) => void;
  onNext: () => void;
  onBack: () => void;
  busy: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  const custom = answer?.custom ? answer.answer : "";

  return (
    <div className="flex flex-col rounded-2xl border border-outline bg-surface p-5 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-info">
        <Sparkles size={14} aria-hidden="true" />
        {t("setup_question_progress", { round, current: index + 1, total })}
      </div>
      <h2 className="mt-2 text-lg font-semibold text-text">{question.question}</h2>

      <fieldset disabled={busy} role="radiogroup" className="mt-4 flex flex-col gap-2">
        {question.options.map((option, optionIndex) => (
          <Choice
            key={`${question.id}-${optionIndex}`}
            selected={!answer?.custom && answer?.answer === option.answer}
            onClick={() => onAnswer({ answer: option.answer, custom: false })}
            title={option.label}
            description={option.description}
            recommended={option.recommended}
          />
        ))}

        <div
          className={clsx(
            "rounded-xl border px-4 py-3 transition",
            answer?.custom ? "border-primary/60 bg-primary/[0.06]" : "border-outline bg-surface",
          )}
        >
          <label className="text-sm font-medium text-text" htmlFor={`custom-${question.id}`}>
            {t("setup_something_else")}
          </label>
          <input
            id={`custom-${question.id}`}
            value={custom}
            onFocus={() => onAnswer({ answer: custom, custom: true })}
            onChange={event => onAnswer({ answer: event.target.value, custom: true })}
            onKeyDown={event => {
              if (event.key === "Enter" && custom.trim() !== "" && !busy) onNext();
            }}
            placeholder={t("setup_custom_placeholder")}
            className="mt-2 w-full rounded-lg border border-outline bg-background px-3 py-2 text-sm text-text outline-none transition focus:border-primary/50 placeholder:text-text-dim"
          />
        </div>
      </fieldset>

      <div className="mt-5 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium text-text-mid transition hover:bg-muted-surface hover:text-text disabled:opacity-40"
        >
          <ArrowLeft size={14} />
          {t("setup_back")}
        </button>
        <button
          type="button"
          data-tour="wizard-next"
          aria-busy={busy}
          onClick={onNext}
          disabled={busy || !answer || answer.answer.trim() === ""}
          className="inline-flex min-w-[7rem] items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-40"
        >
          {busy ? t("reviewing") : t("setup_next")}
          {busy ? (
            <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight size={14} />
          )}
        </button>
      </div>
    </div>
  );
};

const WatchSetupWizard = ({
  review,
  round,
  questionIndex,
  answers,
  onAnswer,
  onNext,
  onBack,
  busy = false,
}: WatchSetupWizardProps) => {
  if (questionIndex !== null && review.questions[questionIndex]) {
    const question = review.questions[questionIndex];
    return (
      <QuestionStep
        question={question}
        round={round}
        index={questionIndex}
        total={review.questions.length}
        answer={answers[question.id]}
        onAnswer={answer => onAnswer(question.id, answer)}
        onNext={onNext}
        onBack={onBack}
        busy={busy}
      />
    );
  }

  return null;
};

export default WatchSetupWizard;
